/**
 * Clips (V1.1-EB-T02): recorded audio an agent can play, as a greeting, a disclosure in a human voice or a hold
 * message (played in calls from V1.1-EB-T03). Upload a file or record one here; play, rename, describe, delete.
 */
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Mic, Pencil, Play, Plus, Square, Trash2 } from "lucide-react";
import { useEffect, useId, useRef, useState, type FormEvent } from "react";
import { data, type Schemas } from "../api/client";
import { Page, Section, useApiQuery } from "../app/Page";
import { useBff, useCan, useMe } from "../app/session";
import { field } from "../knowledge/parts";
import { useFormat, useT } from "../i18n";
import { Button } from "../ui/Button";
import { Empty, ErrorState } from "../ui/states";
import { Table } from "../ui/Table";
import { MAX_SECONDS, startRecording, type Recording } from "./record";

type Clip = Schemas["ClipOut"];
const NAME = /^[a-z0-9][a-z0-9_-]{0,63}$/;

function useRefresh() {
  const queries = useQueryClient();
  const tenant = useMe().tenant_id;
  return () => queries.invalidateQueries({ predicate: (q) => q.queryKey[0] === "clips" && q.queryKey[1] === tenant });
}

export function ClipsPage() {
  const t = useT();
  const format = useFormat();
  const canWrite = useCan("agents:write");
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<Clip | null>(null);
  const clips = useApiQuery("clips", (api) => api.GET("/v1/clips"));
  const add = canWrite && !adding && (
    <Button onClick={() => setAdding(true)}>
      <Plus size={16} aria-hidden="true" />
      {t("clips.add")}
    </Button>
  );
  return (
    <Page title={t("nav.clips")} description={t("clips.description")} actions={add}>
      {adding && <AddClip onDone={() => setAdding(false)} />}
      {editing && <EditClip clip={editing} onDone={() => setEditing(null)} />}
      <Section query={clips}>
        {(list) =>
          list.data.length === 0 ? (
            adding ? null : (
              <Empty title={t("clips.empty")}>
                <p>{t("clips.emptyBody")}</p>
                {add}
              </Empty>
            )
          ) : (
            <Table
              caption={t("clips.table")}
              rows={list.data}
              rowKey={(c) => c.id}
              columns={[
                { header: t("clips.name"), cell: (c) => <span className="font-mono text-xs">{c.name}</span> },
                { header: t("clips.title"), cell: (c) => c.title || "—" },
                { header: t("clips.length"), cell: (c) => format.duration(c.duration_ms) },
                { header: t("clips.transcript"), cell: (c) => <span className="line-clamp-2">{c.transcript || "—"}</span> },
                {
                  header: t("clips.actions"),
                  cell: (c) => <ClipActions clip={c} canWrite={canWrite} onEdit={() => setEditing(c)} />,
                },
              ]}
            />
          )
        }
      </Section>
    </Page>
  );
}

function ClipActions({ clip, canWrite, onEdit }: { clip: Clip; canWrite: boolean; onEdit: () => void }) {
  const t = useT();
  const { api } = useBff();
  const refresh = useRefresh();
  const [url, setUrl] = useState<string | null>(null);
  const load = useMutation({
    mutationFn: async () => {
      const blob = (await data(
        api.GET("/v1/clips/{clip_id}/audio", { params: { path: { clip_id: clip.id } }, parseAs: "blob" }),
      )) as unknown as Blob;
      return URL.createObjectURL(blob);
    },
    onSuccess: setUrl,
  });
  const drop = useMutation({
    mutationFn: () => data(api.DELETE("/v1/clips/{clip_id}", { params: { path: { clip_id: clip.id } } })),
    onSuccess: refresh,
  });
  useEffect(() => () => {
    if (url) URL.revokeObjectURL(url);
  }, [url]);
  return (
    <div className="flex flex-wrap items-center gap-2">
      {url ? (
        <audio src={url} controls autoPlay aria-label={t("clips.playing", { name: clip.name })} className="h-8" />
      ) : (
        <Button variant="secondary" onClick={() => load.mutate()} disabled={load.isPending}
          aria-label={t("clips.play", { name: clip.name })}>
          <Play size={14} aria-hidden="true" />
        </Button>
      )}
      {canWrite && (
        <>
          <Button variant="secondary" onClick={onEdit} aria-label={t("clips.edit", { name: clip.name })}>
            <Pencil size={14} aria-hidden="true" />
          </Button>
          <Button variant="secondary" disabled={drop.isPending} aria-label={t("clips.delete", { name: clip.name })}
            onClick={() => {
              if (window.confirm(t("clips.deleteConfirm", { name: clip.name }))) drop.mutate();
            }}>
            <Trash2 size={14} aria-hidden="true" />
          </Button>
        </>
      )}
      {(load.error || drop.error) && <ErrorState error={load.error ?? drop.error} />}
    </div>
  );
}

/** A file, or a recording made here; a name agents will use for it. */
function AddClip({ onDone }: { onDone: () => void }) {
  const t = useT();
  const { api } = useBff();
  const refresh = useRefresh();
  const id = useId();
  const [source, setSource] = useState<"file" | "record">("file");
  const [name, setName] = useState("");
  const [title, setTitle] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [recorded, setRecorded] = useState<Blob | null>(null);
  const audio = source === "file" ? file : recorded;
  const upload = useMutation({
    mutationFn: async () => {
      if (!audio) return;
      await data(api.POST("/v1/clips", {
        params: { query: { name, filename: file && source === "file" ? file.name : `${name}.wav`,
          ...(title.trim() ? { title: title.trim() } : {}) } },
        body: audio as unknown as string,
        bodySerializer: (body: unknown) => body as BodyInit,
        headers: { "Content-Type": audio.type || "application/octet-stream" },
      }));
    },
    onSuccess: async () => {
      await refresh();
      onDone();
    },
  });
  const ready = NAME.test(name) && audio !== null;
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (ready) upload.mutate();
  };
  return (
    <form aria-labelledby={`${id}-title`} onSubmit={submit}
      className="space-y-3 rounded-lg border border-[var(--color-border)] p-4">
      <h2 id={`${id}-title`} className="font-semibold">{t("clips.add")}</h2>
      <div className="grid gap-3 sm:grid-cols-2">
        <label className="flex flex-col gap-1 text-sm">
          {t("clips.name")}
          <input className={`${field} font-mono`} value={name} onChange={(e) => setName(e.target.value.trim())}
            aria-describedby={`${id}-name`} required />
          <span id={`${id}-name`} className="text-xs text-[var(--color-muted)]">{t("clips.nameHint")}</span>
        </label>
        <label className="flex flex-col gap-1 text-sm">
          {t("clips.title")}
          <input className={field} value={title} onChange={(e) => setTitle(e.target.value)} maxLength={300} />
        </label>
      </div>
      <div role="radiogroup" aria-label={t("clips.addFrom")} className="flex flex-wrap gap-4">
        {(["file", "record"] as const).map((s) => (
          <label key={s} className="flex items-center gap-2 text-sm">
            <input type="radio" name={`${id}-source`} checked={source === s} onChange={() => setSource(s)}
              className="accent-[var(--color-primary)]" />
            {t(`clips.source.${s}`)}
          </label>
        ))}
      </div>
      {source === "file" ? (
        <label className="flex flex-col gap-1 text-sm">
          {t("clips.file")}
          <input type="file" accept=".wav,.flac,.ogg,.opus,.mp3,audio/*"
            onChange={(e) => setFile(e.target.files?.[0] ?? null)} />
        </label>
      ) : (
        <Recorder onRecorded={setRecorded} />
      )}
      {upload.error && <ErrorState error={upload.error} />}
      <div className="flex gap-2">
        <Button type="submit" disabled={!ready || upload.isPending}>{t("clips.upload")}</Button>
        <Button type="button" variant="secondary" onClick={onDone}>{t("clips.cancel")}</Button>
      </div>
    </form>
  );
}

function Recorder({ onRecorded }: { onRecorded: (wav: Blob | null) => void }) {
  const t = useT();
  const format = useFormat();
  const recording = useRef<Recording | null>(null);
  const [state, setState] = useState<"idle" | "starting" | "recording" | "done">("idle");
  const [seconds, setSeconds] = useState(0);
  const [preview, setPreview] = useState<string | null>(null);
  const [denied, setDenied] = useState(false);

  const stop = async () => {
    const r = recording.current;
    recording.current = null;
    if (!r) return;
    const wav = await r.stop();
    setSeconds(r.seconds());
    setPreview(URL.createObjectURL(wav));
    setState("done");
    onRecorded(wav);
  };
  const start = async () => {
    setDenied(false);
    setState("starting");
    onRecorded(null);
    try {
      recording.current = await startRecording(() => void stop());
      setState("recording");
    } catch {
      setDenied(true);
      setState("idle");
    }
  };
  useEffect(() => {
    if (state !== "recording") return;
    const timer = setInterval(() => setSeconds(recording.current?.seconds() ?? 0), 250);
    return () => clearInterval(timer);
  }, [state]);
  useEffect(() => () => {
    if (preview) URL.revokeObjectURL(preview);
  }, [preview]);
  useEffect(() => () => void recording.current?.stop(), []);

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-3">
        {state === "recording" ? (
          <Button type="button" onClick={() => void stop()}>
            <Square size={14} aria-hidden="true" />
            {t("clips.stop")}
          </Button>
        ) : (
          <Button type="button" variant="secondary" disabled={state === "starting"} onClick={() => void start()}>
            <Mic size={14} aria-hidden="true" />
            {state === "done" ? t("clips.recordAgain") : t("clips.record")}
          </Button>
        )}
        <span role="status" className="text-sm text-[var(--color-muted)]">
          {state === "recording" ? t("clips.recording", { time: format.duration(seconds * 1000), max: MAX_SECONDS })
            : state === "done" ? t("clips.recorded", { time: format.duration(seconds * 1000) }) : ""}
        </span>
      </div>
      {denied && <p role="alert" className="text-sm text-[var(--color-danger)]">{t("clips.micDenied")}</p>}
      {preview && state === "done" && <audio src={preview} controls aria-label={t("clips.preview")} className="h-8" />}
    </div>
  );
}

/** Rename it (agents name clips: a renamed clip is not found by its old name), or say what it says. */
function EditClip({ clip, onDone }: { clip: Clip; onDone: () => void }) {
  const t = useT();
  const { api } = useBff();
  const refresh = useRefresh();
  const id = useId();
  const [name, setName] = useState(clip.name);
  const [title, setTitle] = useState(clip.title);
  const [transcript, setTranscript] = useState(clip.transcript);
  const save = useMutation({
    mutationFn: () => data(api.PATCH("/v1/clips/{clip_id}", {
      params: { path: { clip_id: clip.id } }, body: { name, title, transcript },
    })),
    onSuccess: async () => {
      await refresh();
      onDone();
    },
  });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (NAME.test(name)) save.mutate();
  };
  return (
    <form aria-labelledby={`${id}-title`} onSubmit={submit}
      className="space-y-3 rounded-lg border border-[var(--color-border)] p-4">
      <h2 id={`${id}-title`} className="font-semibold">{t("clips.editing", { name: clip.name })}</h2>
      <div className="grid gap-3 sm:grid-cols-2">
        <label className="flex flex-col gap-1 text-sm">
          {t("clips.name")}
          <input className={`${field} font-mono`} value={name} onChange={(e) => setName(e.target.value.trim())}
            aria-describedby={`${id}-rename`} required />
          {name !== clip.name && <span id={`${id}-rename`} className="text-xs text-[var(--color-warning)]">{t("clips.renameNote")}</span>}
        </label>
        <label className="flex flex-col gap-1 text-sm">
          {t("clips.title")}
          <input className={field} value={title} onChange={(e) => setTitle(e.target.value)} maxLength={300} />
        </label>
      </div>
      <label className="flex flex-col gap-1 text-sm">
        {t("clips.transcript")}
        <textarea className={field} rows={3} value={transcript} maxLength={4000}
          onChange={(e) => setTranscript(e.target.value)} aria-describedby={`${id}-transcript`} />
        <span id={`${id}-transcript`} className="text-xs text-[var(--color-muted)]">{t("clips.transcriptHint")}</span>
      </label>
      {save.error && <ErrorState error={save.error} />}
      <div className="flex gap-2">
        <Button type="submit" disabled={!NAME.test(name) || save.isPending}>{t("clips.save")}</Button>
        <Button type="button" variant="secondary" onClick={onDone}>{t("clips.cancel")}</Button>
      </div>
    </form>
  );
}
